import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// GTM Helper Functions
const pushGTMEvent = (eventData) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    // Reset ecommerce state first
    window.dataLayer.push({ ecommerce: null });
    // Push the event
    window.dataLayer.push(eventData);
  }
};

// Helper function to format date with timezone
const formatDateWithTimezone = (date, timeZone) => {
  if (!date) return undefined;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return undefined;
  
  // Format date in the user's timezone
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timeZone || 'UTC'
  }).format(dateObj); // en-CA format gives YYYY-MM-DD
};

const getViewItemData = (listing, author, config) => {
  const { title, publicData, price } = listing.attributes;
  const { listingType, location, optionDuration } = publicData || {};

  // Get price per participant
  const unitPrice = price ? price.amount / 100 : 0; // Convert from cents to euros

  // Get category name
  const listingTypeOptions = config?.listing?.listingTypes
  const categoryName = listingTypeOptions?.find(l => l.listingType === listingType)?.label || 'Experience';

  // Get location/city
  const city = location?.address || location?.city || 'Unknown';

  // Get first price variant
  const variant = `${optionDuration?.[0]?.duration}h` || 'Unknown';

  return {
    event: 'view_item',
    ecommerce: {
      currency: config?.currency || 'EUR',
      value: unitPrice,
      items: [{
        item_id: listing.id.uuid,
        item_name: title,
        item_brand: author?.attributes?.profile?.displayName || '',
        item_category: categoryName,
        // item_variant: variant,
        price: unitPrice,
        quantity: 1,
        location_id: city,
        affiliation: 'From Locals',
      }]
    }
  };
};

const getBeginCheckoutData = (listing, author, bookingData, config) => {
  const { title, publicData, price } = listing.attributes;
  const { listingType, location, additionalLocations } = publicData || {};

  const { 
    seats, 
    duration, 
    durationOptions,
    startDate, 
    additionalLocation 
  } = bookingData || {}

  // Get price per participant
  const unitPrice = price ? price.amount / 100 : 0;
  const currentPriceFromDuration = (durationOptions.find(d => d.duration === duration)?.price)/100 || unitPrice

  const selectedLocation = additionalLocations.filter(loc => additionalLocation.indexOf(loc.description) !== -1)
  const selectedLocationPrice = (selectedLocation?.[0]?.price) / 100 || 0;

  // Calculate total value based on booking type
  const quantity = parseInt(seats) || 1;
  // const totalValue = quantity * unitPrice;
  const totalValue = listingType === "book-seats" ? selectedLocationPrice + (currentPriceFromDuration * quantity )
  : selectedLocationPrice + currentPriceFromDuration;
  
  // Get variant info
  const variant = `${duration}h` || 'Standard';

  // Get category and location
  const listingTypeOptions = config?.listing?.listingTypes
  const categoryName = listingTypeOptions?.find(l => l.listingType === listingType)?.label || 'Experience';
  const city = location?.address || location?.city || 'Unknown';

  // Get timezone from listing
  const timeZone = listing?.attributes?.availabilityPlan?.timezone || 'UTC';
  
  // Format booking date with timezone
  const bookingDate = formatDateWithTimezone(startDate, timeZone);

  return {
    event: 'begin_checkout',
    ecommerce: {
      currency: config?.currency || 'EUR',
      value: totalValue,
      items: [{
        item_id: listing.id.uuid,
        item_name: title,
        item_brand: author?.attributes?.profile?.displayName || '',
        item_category: categoryName,
        item_variant: variant,
        price: unitPrice,
        quantity: listingType === "book-seats" ? parseInt(quantity) : 1,
        location_id: city,
        item_booking_date: bookingDate,
        affiliation: 'From Locals',
      }]
    },
    // Event-level parameters for dual-use fields
    booking_date: bookingDate,
    booking_travelers: parseInt(quantity),
  };
};

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn, H1, AvatarSmall, AvatarMedium,
  CancellationBanner,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';
import SectionDurationMaybe from './SectionDurationMaybe';
import UserCard from './UserCard/UserCard';
import { formatMoney } from '../../util/currency';
import { Avatar } from '../../examples';
import { getAvailableStartTimes } from './helper';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const formApiRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [updatedOptionDuration, setUpdatedOptionDuration] = useState([]);
  const [monthlyTimeSlots, setMonthlyTimeSlots] = useState(false);
  const [isBookingDateChanged, setBookingDateChanged] = useState(false);
  const [viewItemTracked, setViewItemTracked] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));


  // GTM view_item event tracking
  useEffect(() => {
    if (mounted && currentListing.id && currentListing.author && !viewItemTracked) {
      const viewItemData = getViewItemData(currentListing, currentListing.author, config);
      pushGTMEvent(viewItemData);
      setViewItemTracked(true); // Mark as tracked to prevent duplicate calls
    }
  }, [mounted, currentListing.id, currentListing.author, config, viewItemTracked]);

  const filteredListingConfig = listingConfig.listingFields.filter(field => field.key !== "duration");
  const durationListingConfig = listingConfig.listingFields.filter(field => field.key === "duration");
  const timeZone = currentListing?.attributes?.availabilityPlan?.timezone;
  const startTimeInterval = currentListing?.attributes?.publicData?.startTimeInterval;
  const seats = currentListing?.attributes?.publicData?.seats;

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const { optionDuration } = publicData || {};


  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const formatMoneyIfSupportedCurrency = (price, intl) => {
    try {
      return formatMoney(intl, price);
    } catch (e) {
      return `(${price?.currency})`;
    }
  };

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const authorLink =   <NamedLink
    className={css.authorNameLink}
    name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
    params={params}
    to={{ hash: '#author' }}
  >
    {authorDisplayName}
  </NamedLink>


  const { formattedPrice } = priceData(price, config.currency, intl);

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(price, intl)}</span>
  );
  const pricePerUnit = (
    <span className={css.perUnit}>
      <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
    </span>
  );

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;

    // GTM begin_checkout event tracking
    if (!isOwnListing && !isCurrentlyClosed) {

      const bookingData = {
        startDate: values?.bookingStartDate?.date || values.startDate,
        seats: values?.guests || values?.seats || 1,
        duration: (values?.bookingEndTime - values?.bookingStartTime) / (1000 * 60 * 60),
        durationOptions: updatedOptionDuration,
        additionalLocation: values?.additionalLocations || ""
      };

      const checkoutData = getBeginCheckoutData(currentListing, currentListing.author, bookingData, config);      
      pushGTMEvent(checkoutData);
    }

    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const getMonthlyTimeSlots = (values) => {
    setMonthlyTimeSlots(values);

    const formattedOptions =  optionDuration?.filter(option => {

      const startTimeInterval = ['hour'];
      const currentDuration = option.duration;
      const bookingLengthInMinutes = currentDuration*60;


      const availableStartTimes = getAvailableStartTimes({
        intl,
        timeZone,
        bookingStart: values?.startDate,
        timeSlotsOnSelectedDate: values?.timeSlots,
        bookingLengthInMinutes,
        startTimeInterval,
      });

      if(availableStartTimes.length > 0) {
        return option
      }
    });

    setUpdatedOptionDuration(formattedOptions);
  }

  // 2. The function to trigger the change from outside
  const handleExternalChange = (values) => {
    if (formApiRef.current) {
      formApiRef.current.batch(() => {
        formApiRef.current.change('bookingStartTime', parseInt(values.startTime));
        formApiRef.current.change('bookingEndTime', parseInt(values.endTime));
        // formApiRef.current.change('seats', 1);
      });
    }
  };

  const listenBookingDateChange = (values) => {
    setBookingDateChanged(values);
  }


  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            <div className={css.desktopHeading}>
              <H1 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H1>
            </div>
            {showListingImage && (
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
              />
            )}

            <CancellationBanner className={css.cancellationBanner}/>
            {/*<div*/}
            {/*  className={showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct}*/}
            {/*>*/}
            {/*  {showListingImage ? (*/}
            {/*    <H4 as="h1" className={css.orderPanelTitle}>*/}
            {/*      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />*/}
            {/*    </H4>*/}
            {/*  ) : (*/}
            {/*    <H1 as="h1" className={css.orderPanelTitle}>*/}
            {/*      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />*/}
            {/*    </H1>*/}
            {/*  )}*/}
            {/*</div>*/}

            <div className={classNames(css.priceContainer, css.priceContainerMobile)}>
              <p className={css.price}>
                <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
              </p>
            </div>

            <div className={css.author}>
              <AvatarSmall user={currentListing.author} className={classNames(css.providerAvatar, css.providerAvatarMobile)} />
              <AvatarMedium user={currentListing.author} className={classNames(css.providerAvatar, css.providerAvatarDesktop)} />
              <span className={css.providerNameLinked}>
                <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
              </span>
              <span className={css.providerNamePlain}>
                <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
              </span>
            </div>

            <SectionTextMaybe
              heading={<FormattedMessage id="ListingPage.descriptionTitle" />}
              text={description}
              showAsIngress
            />

            <div className={css.sectionDurationDesktop}>
              <SectionDurationMaybe
                monthlyTimeSlots={monthlyTimeSlots}
                durationListingConfig={durationListingConfig}
                startTimeInterval={startTimeInterval}
                timeZone={timeZone}
                intl={intl}
                price={price}
                seats={seats}
                optionDuration={optionDuration}
                updatedOptionDuration={updatedOptionDuration}
                handleExternalChange={handleExternalChange}
                marketplaceCurrency={config.currency}
                isBookingDateChanged={isBookingDateChanged}
                currentListing={currentListing}
              />
            </div>

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={filteredListingConfig}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={classNames(css.productOrderPanel, {
                [css.imagesEnabled]: showListingImage,
              })}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              sectionDuration={
                <div className={css.sectionDurationMobile}>
                  <SectionDurationMaybe
                    monthlyTimeSlots={monthlyTimeSlots}
                    durationListingConfig={durationListingConfig}
                    startTimeInterval={startTimeInterval}
                    timeZone={timeZone}
                    intl={intl}
                    price={price}
                    seats={seats}
                    optionDuration={optionDuration}
                    updatedOptionDuration={updatedOptionDuration}
                    handleExternalChange={handleExternalChange}
                    marketplaceCurrency={config.currency}
                    isBookingDateChanged={isBookingDateChanged}
                    currentListing={currentListing}
                  />
                </div>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
              getMonthlyTimeSlots={getMonthlyTimeSlots}
              formApiRef={formApiRef}
              listenBookingDateChange={listenBookingDateChange}
              optionDuration={optionDuration}
              updatedOptionDuration={updatedOptionDuration}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
